import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, CreditCard, AlertCircle, Check } from 'lucide-react';
import { paymentCardGradient } from '@/components/payment/PaymentCardGradientStyles';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { stripePromise } from '@/lib/stripe';
import { reserveUrl } from '@/lib/apiUrls';
import { mergeSupabaseEdgeHeaders } from '@/lib/reserveActAs';
import { formatStripeErrorMessage } from '@/lib/stripeErrors';
import { fmtAmount } from '@/utils/money';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';

/** Stable per-attempt key so a double-click (or a retry after network error) hits the same Stripe
 *  PaymentIntent instead of creating a new one. Stripe dedupes on this key for 24h, so as long as
 *  the amount/card don't change, retries will return the original PI. */
function newIdempotencyKey() {
  const rnd = (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36);
  return `addfunds-${rnd}`;
}

/** Backwards-compatible wrapper around the shared helper so the rest of this file
 *  keeps reading naturally (`formatStripeError(data)`). The shared helper accepts
 *  the same `{ error, code, decline_code }` shape the server returns, plus Stripe.js
 *  error objects, plus PaymentIntent.last_payment_error — see `src/lib/stripeErrors.js`. */
function formatStripeError(data) {
  return formatStripeErrorMessage(data);
}

const MIN_AMOUNT = 10;
const MAX_AMOUNT = 10000;

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: 'Inter, system-ui, sans-serif',
      fontWeight: '500',
      '::placeholder': {
        color: 'rgba(255, 255, 255, 0.4)',
      },
    },
    invalid: {
      color: '#f87171',
      iconColor: '#f87171',
    },
  },
  hidePostalCode: true,
};

function AddFundsForm({
  onClose,
  paymentMethods,
  defaultPaymentMethod,
  onSuccess,
  onAddCard,
  suggestedAmountCents,
  suggestedAmountLabel,
  onSuccessReturnTo,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  // Pre-fill with the exact shortfall amount when launched from a checkout that needs
  // top-up to complete. Without a suggestion we start blank so the dashboard "Add Funds"
  // flow stays unchanged for general top-ups.
  const suggestedDollars = (Number.isFinite(suggestedAmountCents) && suggestedAmountCents > 0)
    ? Math.max(MIN_AMOUNT, Math.min(MAX_AMOUNT, Math.ceil(suggestedAmountCents) / 100))
    : 0;
  const [amount, setAmount] = useState(suggestedDollars > 0 ? suggestedDollars.toFixed(2) : '');
  const [useNewCard, setUseNewCard] = useState(paymentMethods.length === 0);
  const [selectedCardId, setSelectedCardId] = useState(defaultPaymentMethod || paymentMethods[0]?.id || null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const amountValue = parseFloat(amount) || 0;
  const isValidAmount = amountValue >= MIN_AMOUNT && amountValue <= MAX_AMOUNT;
  const amountCents = Math.round(amountValue * 100);
  const isShortOfBookingTarget = suggestedDollars > 0 && amountValue > 0 && amountValue < suggestedDollars;

  // One key per distinct "attempt" (same amount + card selection). If the user changes either,
  // we generate a fresh key; if they retry after a transient error, they hit the same PI.
  const [idempotencyKey, setIdempotencyKey] = useState(() => newIdempotencyKey());
  useEffect(() => {
    setIdempotencyKey(newIdempotencyKey());
  }, [amountCents, selectedCardId, useNewCard]);

  const confirmPaymentOnServer = useCallback(async (paymentIntentId) => {
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      await fetch(reserveUrl('confirm-payment'), {
        method: 'POST',
        headers: mergeSupabaseEdgeHeaders({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` }),
        body: JSON.stringify({ payment_intent_id: paymentIntentId })
      });
    } catch (e) {
      // Non-blocking — webhook will eventually catch it
    }
  }, []);

  const handleAmountChange = (e) => {
    const value = e.target.value.replace(/[^\d.]/g, '');
    const parts = value.split('.');
    if (parts.length > 2) return;
    if (parts[1]?.length > 2) return;
    setAmount(value);
    setError(null);
  };

  // When launched with a shortfall, the only useful quick-amount is "exactly enough to book".
  // Show one prominent button (the shortfall) plus a couple of "with buffer" options. Without
  // a shortfall, fall back to the generic top-up presets.
  const quickAmounts = suggestedDollars > 0
    ? Array.from(new Set([
        Math.ceil(suggestedDollars),
        Math.ceil(suggestedDollars) + 50,
        Math.ceil(suggestedDollars) + 100,
      ])).filter((v) => v <= MAX_AMOUNT)
    : [25, 50, 100, 250, 500];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsProcessing(true);

    try {
      if (!isValidAmount) {
        throw new Error(`Amount must be between $${MIN_AMOUNT} and $${MAX_AMOUNT.toLocaleString()}`);
      }

      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('Please sign in to continue');
      }

      const response = await fetch(reserveUrl('create-payment-intent'), {
        method: 'POST',
        headers: mergeSupabaseEdgeHeaders({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        }),
        body: JSON.stringify({
          amount_cents: amountCents,
          payment_method_id: useNewCard ? undefined : selectedCardId,
          description: 'Reserve Top-Up',
          client_request_id: idempotencyKey,
        })
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success === false) {
        // Rotate the idempotency key on a hard failure so the next click gets a fresh PI
        // (otherwise Stripe returns the same failed PI forever for this key).
        setIdempotencyKey(newIdempotencyKey());
        throw new Error(formatStripeError(data) || data.error || 'Failed to create payment');
      }

      // Single helper so every success path tells the parent to refresh balance, closes
      // the modal, and (when launched with a return-to URL) takes the user back to the
      // checkout that needed the top-up. We let parent state updates flush before navigating
      // so the dashboard balance UI doesn't briefly show stale numbers.
      const finishSuccess = async () => {
        onSuccess?.();
        onClose();
        if (onSuccessReturnTo) {
          setTimeout(() => navigate(onSuccessReturnTo), 0);
        }
      };

      if (data.status === 'succeeded') {
        await confirmPaymentOnServer(data.payment_intent_id);
        toast.success(`$${fmtAmount(amountValue)} added to your Reserve!`);
        await finishSuccess();
        return;
      }

      if (useNewCard) {
        if (!stripe || !elements) throw new Error('Payment system not ready');
        const cardElement = elements.getElement(CardElement);
        if (!cardElement) throw new Error('Card input not found');

        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
          data.client_secret,
          {
            payment_method: { card: cardElement },
            setup_future_usage: 'off_session',
          }
        );

        if (confirmError) {
          setIdempotencyKey(newIdempotencyKey());
          throw new Error(formatStripeError(confirmError) || confirmError.message);
        }
        if (paymentIntent?.status === 'succeeded') {
          await confirmPaymentOnServer(paymentIntent.id);
          toast.success(`$${fmtAmount(amountValue)} added to your Reserve!`);
          await finishSuccess();
        } else {
          // Most common: status='processing' (ACH-like) — webhook will finalize.
          toast.info('Payment is being processed. Your balance will update shortly.');
          await finishSuccess();
        }
        return;
      }

      // Saved-card path. On 3DS challenge Stripe returns status='requires_action' with
      // next_action.type='use_stripe_sdk'. handleCardAction is purpose-built for that — it
      // surfaces the authentication UI without touching the attached payment_method. If we
      // already got 'succeeded' or anything else we skip the SDK call.
      if (!stripe) throw new Error('Payment system not ready');
      if (data.status === 'requires_action' && data.client_secret) {
        const { error: actionError, paymentIntent } = await stripe.handleCardAction(data.client_secret);
        if (actionError) {
          setIdempotencyKey(newIdempotencyKey());
          throw new Error(formatStripeError(actionError) || actionError.message);
        }
        if (paymentIntent?.status !== 'succeeded') {
          setIdempotencyKey(newIdempotencyKey());
          throw new Error('Authentication was not completed. Please try again or use a different card.');
        }
        await confirmPaymentOnServer(paymentIntent.id);
        toast.success(`$${fmtAmount(amountValue)} added to your Reserve!`);
        await finishSuccess();
        return;
      }

      // Any other non-succeeded status on a saved card (requires_payment_method, canceled, etc.)
      // means the card was rejected. Rotate the key and ask the user to try again.
      setIdempotencyKey(newIdempotencyKey());
      throw new Error('Your card could not be charged. Please try a different payment method.');
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* When launched from a checkout that needs top-up, lead with the exact shortfall —
          customers who only see the generic Add Funds form regularly underpay (e.g. $54
          toward a $550 booking) and then think their money was lost. */}
      {suggestedDollars > 0 && (
        <div className="p-4 bg-brand-500/10 border border-brand-500/30 rounded-2xl">
          <p className="text-brand-200 text-sm font-medium">
            Top up at least ${fmtAmount(suggestedDollars)} to finish booking
            {suggestedAmountLabel ? ` your ${suggestedAmountLabel}` : ''}.
          </p>
          <p className="text-brand-200/70 text-xs font-normal mt-1.5">
            {`Adding less won't be enough — your card will be charged but the booking won't go through. Reserve credits stay in your account either way.`}
          </p>
        </div>
      )}

      {/* Amount input */}
      <div className="space-y-3">
        <Label className="text-white/80 font-normal">Amount</Label>
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60 text-lg">$</span>
          <Input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={handleAmountChange}
            placeholder="0.00"
            className="pl-8 h-14 text-2xl font-light bg-white/5 border-white/10 text-white rounded-2xl focus:border-white/30"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {quickAmounts.map((qa) => (
            <button
              key={qa}
              type="button"
              onClick={() => setAmount(String(qa))}
              className={`px-4 py-2 rounded-full text-sm font-normal transition-colors ${
                amount === String(qa)
                  ? 'bg-white text-black'
                  : 'bg-white/10 text-white/80 hover:bg-white/20'
              }`}
            >
              ${qa}
            </button>
          ))}
        </div>
        {amount && !isValidAmount && (
          <p className="text-red-400 text-sm flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            Amount must be between ${MIN_AMOUNT} and ${MAX_AMOUNT.toLocaleString()}
          </p>
        )}
        {isShortOfBookingTarget && (
          <p className="text-brand-300 text-sm flex items-start gap-1.5">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>
              {`$${fmtAmount(amountValue)} isn't enough to complete your booking — you'd still be $${fmtAmount(suggestedDollars - amountValue)} short.`}{' '}
              <button
                type="button"
                onClick={() => setAmount(suggestedDollars.toFixed(2))}
                className="underline font-medium hover:text-brand-200"
              >
                Use ${fmtAmount(suggestedDollars)} instead
              </button>
            </span>
          </p>
        )}
      </div>

      {/* Payment method selection */}
      <div className={paymentCardGradient.container}>
        <p className="text-white/70 text-xs font-normal uppercase tracking-wider mb-4">Payment Method</p>
        {paymentMethods.length > 0 && !useNewCard ? (
          <div className="space-y-2.5">
            {paymentMethods.map((pm) => (
              <button
                key={pm.id}
                type="button"
                onClick={() => { setSelectedCardId(pm.id); setUseNewCard(false); }}
                className={`${paymentCardGradient.cardRowBase} ${
                  selectedCardId === pm.id ? paymentCardGradient.cardSelected : paymentCardGradient.cardUnselected
                }`}
              >
                <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                  selectedCardId === pm.id ? paymentCardGradient.iconSelected : paymentCardGradient.iconUnselected
                }`}>
                  <CreditCard className={`w-5 h-5 ${selectedCardId === pm.id ? paymentCardGradient.iconColorSelected : paymentCardGradient.iconColorUnselected}`} />
                </div>
                <div className="flex-1 text-left">
                  <span className="font-medium text-white capitalize">{pm.brand}</span>
                  <span className="text-white/50 font-mono text-sm ml-2">•••• {pm.last4}</span>
                </div>
                {selectedCardId === pm.id && (
                  <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${paymentCardGradient.checkBadge}`}>
                    <Check className={`w-3.5 h-3.5 ${paymentCardGradient.checkIcon}`} strokeWidth={2.5} />
                  </div>
                )}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setUseNewCard(true)}
              className={paymentCardGradient.addNewCard}
            >
              <div className={paymentCardGradient.addNewCardIcon}>
                <CreditCard className="w-5 h-5 text-white/40" />
              </div>
              Use a new card
            </button>
          </div>
        ) : (
          <div className="space-y-4 min-w-0">
            <div className={paymentCardGradient.cardElementWrapper}>
              <CardElement options={CARD_ELEMENT_OPTIONS} />
            </div>
            {paymentMethods.length > 0 && (
              <button
                type="button"
                onClick={() => { setUseNewCard(false); setSelectedCardId(paymentMethods[0]?.id); }}
                className={paymentCardGradient.useSavedCardLink}
              >
                ← Use saved card instead
              </button>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl">
          <p className="text-red-400 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            {error}
          </p>
        </div>
      )}

      {/* Important Disclaimer */}
      <div className="p-3 bg-white/5 border border-white/10 rounded-xl">
        <p className="text-white/50 text-xs font-normal text-center">
          <strong className="text-white/85">Credits are non-withdrawable and have no cash value.</strong>
        </p>
      </div>

      <Button
        type="submit"
        disabled={!isValidAmount || isProcessing || (!useNewCard && !selectedCardId)}
        className="w-full bg-white text-black hover:bg-white/90 rounded-full py-6 text-base font-normal disabled:opacity-50"
      >
        {isProcessing ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Processing...
          </>
        ) : (
          <>Add ${fmtAmount(amountValue)} to Reserve</>
        )}
      </Button>
    </form>
  );
}

export default function AddFundsModal({
  isOpen,
  onClose,
  paymentMethods,
  defaultPaymentMethod,
  onSuccess,
  onAddCard,
  stripePromise,
  suggestedAmountCents,
  suggestedAmountLabel,
  onSuccessReturnTo,
}) {
  const hasSuggestion = Number.isFinite(suggestedAmountCents) && suggestedAmountCents > 0;
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-brand-950/95 border-brand-800/45 backdrop-blur-xl text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-light">
            {hasSuggestion ? 'Top up to finish booking' : 'Add Funds'}
          </DialogTitle>
          <DialogDescription className="text-white/60 font-normal">
            {hasSuggestion
              ? 'Add the amount needed to complete your booking — we\'ll take you back to checkout.'
              : 'Add money to your Reserve balance'}
          </DialogDescription>
        </DialogHeader>

        {stripePromise ? (
          <Elements stripe={stripePromise}>
            <AddFundsForm
              onClose={onClose}
              paymentMethods={paymentMethods}
              defaultPaymentMethod={defaultPaymentMethod}
              onSuccess={onSuccess}
              onAddCard={onAddCard}
              suggestedAmountCents={suggestedAmountCents}
              suggestedAmountLabel={suggestedAmountLabel}
              onSuccessReturnTo={onSuccessReturnTo}
            />
          </Elements>
        ) : (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-white/40" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
